import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Zap,
  Users,
  Settings,
  ArrowRight,
  Shield,
  BarChart3,
} from 'lucide-react';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const adminSections = [
    {
      title: 'AI Provider Management',
      description: 'Configure AI providers, manage API keys, and set feature routing',
      icon: Zap,
      color: 'indigo',
      path: '/dashboard/admin/ai-providers',
      status: 'active',
    },
    {
      title: 'User Management',
      description: 'View all users, manage subscriptions, and monitor usage',
      icon: Users,
      color: 'blue',
      path: '/dashboard/admin/users',
      status: 'active',
    },
    {
      title: 'Platform ROI',
      description: 'Platform-wide metrics, MRR tracking, and ROI analytics',
      icon: BarChart3,
      color: 'green',
      path: '/dashboard/admin/platform-roi',
      status: 'active',
    },
    {
      title: 'Conversion Funnel',
      description: 'Server-side drop-off analysis: landing → preview → upload → signup → download',
      icon: BarChart3,
      color: 'indigo',
      path: '/dashboard/admin/funnel',
      status: 'active',
    },
    {
      title: 'System Settings',
      description: 'Configure system-wide settings and preferences',
      icon: Settings,
      color: 'gray',
      path: '#',
      status: 'coming_soon',
    },
  ];

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="max-w-6xl">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-indigo-400" />
          <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
        </div>
        <p className="text-text-secondary">
          Manage system configuration, AI providers, and administrative settings
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminSections.map((section) => {
          const Icon = section.icon;
          const isComingSoon = section.status === 'coming_soon';

          return (
            <button
              key={section.title}
              onClick={() => !isComingSoon && navigate(section.path)}
              disabled={isComingSoon}
              className={`
                relative bg-bg-light p-6 rounded-xl border border-text-muted/20
                transition-all duration-200
                ${
                  isComingSoon
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 cursor-pointer'
                }
              `}
            >
              {isComingSoon && (
                <div className="absolute top-4 right-4 px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-semibold rounded">
                  Coming Soon
                </div>
              )}

              <div className="flex items-start justify-between mb-4">
                <div
                  className={`p-3 rounded-lg bg-${section.color}-500/10 text-${section.color}-400`}
                >
                  <Icon className="w-6 h-6" />
                </div>
                {!isComingSoon && (
                  <ArrowRight className="w-5 h-5 text-text-muted group-hover:text-primary transition-colors" />
                )}
              </div>

              <h3 className="text-lg font-semibold text-white mb-2">{section.title}</h3>
              <p className="text-sm text-text-secondary">{section.description}</p>
            </button>
          );
        })}
      </div>

      <div className="mt-8 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
        <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
          <Shield className="w-5 h-5 text-indigo-400" />
          Admin Access
        </h3>
        <p className="text-sm text-text-secondary">
          You are logged in as: <span className="text-white font-medium">{user?.email}</span>
        </p>
      </div>
    </div>
  );
};

export default AdminPanel;
